import React, { FC } from 'react';
import {
  SUBTYPE as POST_SUBTYPE,
  SUBTYPE_INSTAGRAM_STORY,
  TYPE_VIDEO,
} from '@kontentino/kontentino-constants/Posts';
import { VIDEO_STATUS_AVAILABLE } from '@kontentino/kontentino-constants/Video';
import PostComposerContentText from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentText';
import PostComposerContentInstagramShareToFeed from 'app/modules/posts/modules/post/components/composer/postComposerContent/postComposerContentInstagram/PostComposerContentInstagramShareToFeed';
import PostComposerContentInstagramLocation from 'app/modules/posts/modules/post/components/composer/postComposerContent/postComposerContentInstagram/PostComposerContentInstagramLocation';
import PostComposerContentInstagramCollaborators from 'app/modules/posts/modules/post/components/composer/postComposerContent/postComposerContentInstagram/PostComposerContentInstagramCollaborators';
import PostComposerContentMediaAttachments from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentMediaAttachemnts';
import { PostComposerContentProps } from 'app/modules/posts/modules/post/components/composer/PostComposerContent';
import PostComposerContentFirstComment from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentFirstComment';
import PostComposerContentInstagramAudioNotice from 'app/modules/posts/modules/post/components/composer/postComposerContent/postComposerContentInstagram/PostComposerContentInstagramAudioNotice';
import PostsConfig from 'app/config/posts';
import { PseudoPostType } from 'constants/post';

type Props = PostComposerContentProps;

const PostComposerContentInstagram: FC<Props> = ({
  value,
  onChange,
  onTextEditorInit,
  onTranslationEditorInit,
  swapTextTranslation,
}) => {
  function getFieldsConfig() {
    return {
      canShowIGReelAdditionalFields: value.subtype === POST_SUBTYPE.REEL,
      canShowCustomThumbnailSlider:
        value.attachments.cards.length === 1 &&
        value.type === TYPE_VIDEO &&
        value.attachments.cards[0]?.medium?.type === 'video' &&
        value.attachments.cards[0]?.medium?.status === VIDEO_STATUS_AVAILABLE,
      canShowLocationInput:
        value.subtype !== POST_SUBTYPE.REEL &&
        value.subtype !== POST_SUBTYPE.INSTAGRAM_STORY,
      canShowCollaboratorsInput: value.subtype !== SUBTYPE_INSTAGRAM_STORY,
    };
  }

  const fieldsConfig = getFieldsConfig();

  function getTextDisabledReason() {
    if (
      value.subtype === SUBTYPE_INSTAGRAM_STORY &&
      value.attachments.publishingMethod === 'direct'
    ) {
      return `This method doesn't support the function of adding text. If you want to create a text, switch to the Manual Publishing method.`;
    }
  }

  return (
    <>
      <PostComposerContentInstagramAudioNotice
        pageType={value.page.type}
        subtype={value.subtype}
      />
      <PostComposerContentText
        value={value}
        onChange={onChange}
        onTextEditorInit={onTextEditorInit}
        onTranslationEditorInit={onTranslationEditorInit}
        swapTextTranslation={swapTextTranslation}
        disabled={getTextDisabledReason()}
      />
      {value.subtype !== SUBTYPE_INSTAGRAM_STORY && (
        <PostComposerContentFirstComment onChange={onChange} value={value} />
      )}

      <PostComposerContentMediaAttachments
        canSetupVideoThumbnail={fieldsConfig.canShowCustomThumbnailSlider}
        value={value}
        onChange={onChange}
        allowedFiles={
          [PseudoPostType.Video, PseudoPostType.Reel].includes(value.pseudoType)
            ? ['video']
            : ['image', 'video']
        }
        maxCount={
          [PseudoPostType.Story, PseudoPostType.PhotoGallery].includes(
            value.pseudoType,
          )
            ? PostsConfig.MAX_FILES_TO_ACCEPT
            : PostsConfig.MIN_FILES_TO_ACCEPT
        }
      />

      {fieldsConfig.canShowIGReelAdditionalFields && (
        <PostComposerContentInstagramShareToFeed
          value={value.attachments}
          onChange={(updater) =>
            onChange((prev) => ({
              attachments: {
                ...prev.attachments,
                ...updater(prev.attachments),
              },
            }))
          }
        />
      )}

      {fieldsConfig.canShowLocationInput && (
        <PostComposerContentInstagramLocation
          pageId={value.page.id}
          value={value.attachments.location}
          onChange={(location) => {
            onChange((prev) => ({
              attachments: {
                ...prev.attachments,
                location,
              },
            }));
          }}
        />
      )}

      {fieldsConfig.canShowCollaboratorsInput && (
        <PostComposerContentInstagramCollaborators
          value={value.attachments.collaborators || []}
          onChange={(collaborators) => {
            onChange((prev) => ({
              ...prev,
              attachments: {
                ...prev.attachments,
                collaborators,
              },
            }));
          }}
        />
      )}
    </>
  );
};

export default PostComposerContentInstagram;
